import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Card, Button, Radio, message } from 'antd'
import { UserOutlined, BookOutlined } from '@ant-design/icons'
import { SET_USER } from '../../actions/auth'
import usersService from '../../services/users'
import Spinner from '../../components/Spinner'
import subscribeUser from '../../subscription'

const OAuthRoleSelection = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const [role, setRole] = useState(null)
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState(null)

  useEffect(() => {
    // Get token from URL query params
    const urlParams = new URLSearchParams(location.search)
    const tokenParam = urlParams.get('token')

    if (!tokenParam) {
      message.error('No authentication token found.')
      history.push('/login')
      return
    }

    setToken(tokenParam)
  }, [location, history])

  const handleSubmit = async () => {
    if (!role) {
      message.warning('Please select a role')
      return
    }

    if (!token) {
      message.error('Authentication token is missing')
      history.push('/login')
      return
    }

    setLoading(true)

    try {
      // Update user role
      const response = await usersService.updateOAuthRole(role, token)

      if (response && response.user && response.token) {
        const userData = {
          user: response.user,
          token: response.token
        }

        // Store in localStorage
        window.localStorage.setItem('WiseAcademy-user', JSON.stringify(userData))
        window.localStorage.setItem('WiseAcademy-token', response.token)

        // Dispatch to Redux
        dispatch({ type: SET_USER, user: userData })

        // Subscribe to notifications
        subscribeUser()

        message.success(`Successfully registered as ${role === 'instructor' ? 'Instructor' : 'Student'}!`)
        history.push('/app')
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Error updating role:', error)
      const errorMsg = error.response?.data?.message || 'Failed to update role. Please try again.'
      message.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
        title={
          <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}>
            Select Your Role
          </div>
        }
      >
        <div style={{ marginBottom: '24px', textAlign: 'center', color: '#666' }}>
          Please select your role to continue. This helps us personalize your experience.
        </div>

        <Radio.Group
          onChange={(e) => setRole(e.target.value)}
          value={role}
          style={{ width: '100%', marginBottom: '24px' }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Radio value="student" style={{ width: '100%', padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BookOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Student</div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Enroll in courses and learn</div>
                </div>
              </div>
            </Radio>
          </div>

          <div>
            <Radio value="instructor" style={{ width: '100%', padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <UserOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Instructor</div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Create and manage courses</div>
                </div>
              </div>
            </Radio>
          </div>
        </Radio.Group>

        <Button
          type="primary"
          block
          size="large"
          onClick={handleSubmit}
          loading={loading}
          disabled={!role}
          style={{ height: '48px', fontSize: '16px' }}
        >
          Continue
        </Button>
      </Card>
    </div>
  )
}

export default OAuthRoleSelection

